import React, { useEffect, useState } from "react";
import EvacuationTimesTable from "../../PublicPodcastScreens/EvacuationTimeTable";
import NextStepsVoiceAssistantPanel from "../../PublicPodcastScreens/NextStepsVoiceAssistantPanel";
import {
  DesktopDownloadNextStepPrompt,
  DownloadSharePrompt,
  ResourceStepCard,
  ShareStepCard,
  SimulationPdfDownloadSection
} from "./NextStepsCards";

const NextStepsModal = ({
  isMobile,
  uiText,
  activeStepsTab,
  setActiveStepsTab,
  onClose,
  onReplay,
  showEvacuationTimesTab,
  enableVoiceAssistant,
  nextStepsVoiceAssistant,
  guidedNextSteps,
  activeNextStep,
  activeNextStepIndex,
  setActiveNextStepIndex,
  completedNextStepSet,
  hiddenNextStepKeys,
  setOpenInstructionKey,
  stopResourcePlayback,
  toggleGuidedStepDetails,
  isPlayableSegment,
  segmentDuration,
  resourceProgress,
  activeResourceKey,
  resourceIsPlaying,
  playResourceSegment,
  seekWithinResourceSegment,
  openResourceAndPlay,
  markNextStepComplete,
  skipActiveGuidedStep,
  resourceUI,
  shareWithNeighbors,
  faqList,
  simulationPdfUrl,
  routeTableRows,
  routeTableLoading,
  routeTableError,
  nearestNodeId,
  onPdfAnalytics,
  onSendAssistantStepContext
}) => {
  const [showDownloadSharePrompt, setShowDownloadSharePrompt] = useState(false);
  const guidedText = uiText.guidedNextSteps || {};

  useEffect(() => {
    if (!isMobile && activeStepsTab === "faq") {
      setActiveStepsTab("next");
    }
  }, [activeStepsTab, isMobile, setActiveStepsTab]);

  const goBack = () => {
    stopResourcePlayback();
    setOpenInstructionKey(null);
    setActiveNextStepIndex((prev) => Math.max(prev - 1, 0));
  };

  const jumpToStep = (idx) => {
    stopResourcePlayback();
    setOpenInstructionKey(null);
    setActiveNextStepIndex(idx);
  };

  const totalSteps = guidedNextSteps.length;
  const nextUntakenStepIndex = guidedNextSteps.findIndex(
    (step) => step?.key && !completedNextStepSet.has(step.key)
  );
  const promptNextStepIndex = nextUntakenStepIndex >= 0
    ? nextUntakenStepIndex
    : Math.max(Math.min(activeNextStepIndex, guidedNextSteps.length - 1), 0);
  const promptNextStep = guidedNextSteps[promptNextStepIndex] || activeNextStep;

  const closeDownloadPrompt = () => {
    setShowDownloadSharePrompt(false);
    setActiveStepsTab("next");
  };

  const takePromptNextStep = () => {
    if (!promptNextStep) {
      closeDownloadPrompt();
      return;
    }

    setActiveNextStepIndex(promptNextStepIndex);

    if (promptNextStep.type === "share") {
      shareWithNeighbors();
      markNextStepComplete(promptNextStep.key);
      closeDownloadPrompt();
      return;
    }

    const seg = promptNextStep.seg;
    const meta = promptNextStep.meta || {};
    if (seg) {
      if (enableVoiceAssistant) {
        onSendAssistantStepContext({
          stepKey: seg.key,
          stepTitle: meta.title || seg.key,
          action: "open_link",
          href: meta.href
        });
      }
      openResourceAndPlay(seg, meta.href);
      markNextStepComplete(seg.key);
    }
    closeDownloadPrompt();
  };

  const skipPromptNextStep = () => {
    setActiveNextStepIndex((prev) =>
      promptNextStepIndex === prev
        ? Math.min(prev + 1, Math.max(guidedNextSteps.length - 1, 0))
        : prev
    );
    closeDownloadPrompt();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: isMobile ? "fixed" : "absolute",
        inset: 0,
        background: isMobile ? "#121720" : "rgba(0,0,0,0.58)",
        display: "flex",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: isMobile ? "stretch" : "center",
        zIndex: 50
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: isMobile ? "100vw" : "min(920px, 96vw)",
          height: isMobile ? "100dvh" : "auto",
          maxHeight: isMobile ? "100dvh" : "86vh",
          background: "#121720",
          color: "#f8fafc",
          borderRadius: isMobile ? 0 : 12,
          boxShadow: isMobile ? "none" : "0 12px 28px rgba(0,0,0,.28)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,255,255,0.08)"
          }}
        >
          <div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{uiText.takeActionTitle}</div>
            {!isMobile && (
              <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
                {uiText.takeActionSubtitle}
              </div>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <button
              onClick={onReplay}
              style={{
                background: "#0b5fff",
                color: "#fff",
                border: "none",
                padding: "8px 12px",
                borderRadius: 8,
                cursor: "pointer",
                fontWeight: 700
              }}
              aria-label="Play the podcast again from the start"
              title="Play it again"
            >
              {uiText.replay}
            </button>
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                color: "#e5e7eb",
                border: "none",
                fontSize: 22,
                lineHeight: 1,
                cursor: "pointer",
                padding: 6,
                borderRadius: 8
              }}
              aria-label="Close"
              title="Close"
            >
              &times;
            </button>
          </div>
        </div>

        {!showDownloadSharePrompt && (
          <div
            style={{
              padding: 8,
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              gap: 8
            }}
          >
            {[
              { key: "next", label: uiText.nextSteps },
              isMobile ? { key: "faq", label: "FAQs" } : null,
              showEvacuationTimesTab ? { key: "times", label: uiText.evacTimesTab } : null
            ].filter(Boolean).map((t) => {
              const active = activeStepsTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveStepsTab(t.key)}
                  style={{
                    appearance: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "8px 12px",
                    borderRadius: 8,
                    fontWeight: 600,
                    background: active ? "#111827" : "transparent",
                    color: active ? "#fff" : "#cbd5e1",
                    outline: "none"
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
        )}

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: showDownloadSharePrompt ? 0 : "16px",
            display: "flex",
            flexDirection: "column",
            gap: 16
          }}
        >
          {showDownloadSharePrompt ? (
            isMobile ? (
              <DownloadSharePrompt
                isMobile={isMobile}
                onShare={(hasShared) => {
                  onPdfAnalytics?.(
                    hasShared
                      ? "simulation_pdf_download_share_again"
                      : "simulation_pdf_download_share_prompt_yes"
                  );
                  shareWithNeighbors();
                }}
                onDismiss={(hasShared) => {
                  onPdfAnalytics?.(
                    hasShared
                      ? "simulation_pdf_download_share_prompt_done"
                      : "simulation_pdf_download_share_prompt_no"
                  );
                  closeDownloadPrompt();
                }}
              />
            ) : (
              <DesktopDownloadNextStepPrompt
                guidedNextSteps={guidedNextSteps}
                guidedText={guidedText}
                onSkip={skipPromptNextStep}
                onTake={takePromptNextStep}
                promptStep={promptNextStep}
                promptStepIndex={promptNextStepIndex}
                resourceUI={resourceUI}
              />
            )
          ) : activeStepsTab === "next" && (
            <>
              {enableVoiceAssistant && (
                <NextStepsVoiceAssistantPanel
                  isEnabled={nextStepsVoiceAssistant.isEnabled}
                  isConnecting={nextStepsVoiceAssistant.isConnecting}
                  isActive={nextStepsVoiceAssistant.isActive}
                  error={nextStepsVoiceAssistant.error}
                  displayPrompt={nextStepsVoiceAssistant.displayPrompt}
                  assistantTranscript={nextStepsVoiceAssistant.assistantTranscript}
                  remoteAudioRef={nextStepsVoiceAssistant.remoteAudioRef}
                  onEnable={nextStepsVoiceAssistant.enableAssistant}
                  onDisable={nextStepsVoiceAssistant.disableAssistant}
                />
              )}

              <div style={{ display: "grid", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                  <div
                    style={{
                      flex: 1,
                      height: 8,
                      borderRadius: 999,
                      background: "rgba(255,255,255,0.12)",
                      overflow: "hidden"
                    }}
                  >
                    <div
                      style={{
                        width: `${totalSteps ? ((activeNextStepIndex + 1) / totalSteps) * 100 : 0}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: "#2f83ff"
                      }}
                    />
                  </div>
                  <div style={{ color: "#cbd5e1", fontWeight: 800, minWidth: 54, textAlign: "right" }}>
                    {Math.min(activeNextStepIndex + 1, totalSteps)} / {totalSteps}
                  </div>
                </div>

                {!isMobile && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    {guidedNextSteps.map((step, idx) => {
                      const isCurrent = idx === activeNextStepIndex;
                      const isDone = completedNextStepSet.has(step.key);
                      return (
                        <button
                          key={step.key}
                          type="button"
                          onClick={() => jumpToStep(idx)}
                          style={{
                            border: `1px solid ${isCurrent ? "rgba(47,131,255,0.75)" : "rgba(255,255,255,0.16)"}`,
                            background: isCurrent ? "rgba(47,131,255,0.16)" : "rgba(255,255,255,0.04)",
                            color: "#ffc400",
                            borderRadius: 999,
                            padding: "8px 13px",
                            cursor: "pointer",
                            fontSize: 18,
                            fontWeight: 800
                          }}
                        >
                          {isDone ? "✓ " : ""}
                          {step.meta?.title || (step.type === "share" ? resourceUI.share : step.key)}
                        </button>
                      );
                    })}
                  </div>
                )}

                <div
                  style={{
                    border: "1px solid #0877dc",
                    borderRadius: 18,
                    padding: isMobile ? 16 : 22,
                    background: "#0877dc",
                    display: "flex",
                    alignItems: "center",
                    gap: 18
                  }}
                >
                  <div>
                    <div style={{ color: "#ffffff", fontSize: isMobile ? 16 : 22, lineHeight: 1.3, fontWeight: 800 }}>
                      {guidedText.timeCallout || "3 minutes now can save your life later"}
                    </div>
                  </div>
                </div>

                {activeNextStep?.type === "resource" && (
                  <ResourceStepCard
                    activeNextStep={activeNextStep}
                    activeNextStepIndex={activeNextStepIndex}
                    activeResourceKey={activeResourceKey}
                    canPlay={isPlayableSegment(activeNextStep.seg)}
                    durationSec={segmentDuration(activeNextStep.seg)}
                    guidedNextSteps={guidedNextSteps}
                    hiddenNextStepKeys={hiddenNextStepKeys}
                    isMobile={isMobile}
                    onBack={goBack}
                    onOpenResource={(seg, meta) => {
                      if (enableVoiceAssistant) {
                        onSendAssistantStepContext({
                          stepKey: seg.key,
                          stepTitle: meta.title || seg.key,
                          action: "open_link",
                          href: meta.href
                        });
                      }
                      openResourceAndPlay(seg, meta.href);
                      markNextStepComplete(seg.key);
                    }}
                    playResourceSegment={playResourceSegment}
                    resourceIsPlaying={resourceIsPlaying}
                    resourceProgress={resourceProgress}
                    resourceUI={resourceUI}
                    seekWithinResourceSegment={seekWithinResourceSegment}
                    skipActiveGuidedStep={skipActiveGuidedStep}
                    stopResourcePlayback={stopResourcePlayback}
                    toggleGuidedStepDetails={toggleGuidedStepDetails}
                    guidedText={guidedText}
                  />
                )}

                {activeNextStep?.type === "share" && (
                  <ShareStepCard
                    activeNextStep={activeNextStep}
                    activeNextStepIndex={activeNextStepIndex}
                    guidedNextSteps={guidedNextSteps}
                    isMobile={isMobile}
                    markNextStepComplete={markNextStepComplete}
                    onBack={goBack}
                    resourceUI={resourceUI}
                    shareWithNeighbors={shareWithNeighbors}
                    skipActiveGuidedStep={skipActiveGuidedStep}
                    uiText={uiText}
                    guidedText={guidedText}
                  />
                )}
              </div>

              {simulationPdfUrl && (
                <SimulationPdfDownloadSection
                  pdfUrl={simulationPdfUrl}
                  isMobile={isMobile}
                  onPdfAnalytics={onPdfAnalytics}
                  onDownloadComplete={() => {
                    setShowDownloadSharePrompt(true);
                  }}
                />
              )}

              <section
                aria-labelledby="disclaimer-title"
                style={{
                  marginTop: 6,
                  paddingTop: 10,
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  textAlign: "left"
                }}
              >
                <h3
                  id="disclaimer-title"
                  style={{ fontSize: 20, fontWeight: 800, margin: "0 0 10px 0" }}
                >
                  {uiText.disclaimerTitle}
                </h3>
                <ul
                  style={{
                    listStyleType: "disc",
                    listStylePosition: "outside",
                    margin: 0,
                    paddingLeft: "1.25rem",
                    color: "#e5e7eb",
                    lineHeight: 1.6
                  }}
                >
                  {(uiText.disclaimerBullets || []).map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </section>

              {!isMobile && <FaqSection faqList={faqList} />}
            </>
          )}

          {isMobile && activeStepsTab === "faq" && (
            <FaqSection faqList={faqList} showHeading={false} />
          )}

          {showEvacuationTimesTab && activeStepsTab === "times" && (
            <EvacuationTimesTable
              rows={routeTableRows}
              loading={routeTableLoading}
              error={routeTableError}
              nodeId={nearestNodeId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const FaqSection = ({ faqList, showHeading = true }) => (
  <section
    aria-label="Frequently asked questions"
    style={{
      marginTop: showHeading ? 6 : 0,
      paddingTop: showHeading ? 10 : 0,
      borderTop: showHeading ? "1px solid rgba(255,255,255,0.08)" : "none"
    }}
  >
    {showHeading && (
      <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 10 }}>FAQs</div>
    )}
    {(faqList || []).map((qa) => (
      <details
        key={qa.key || qa.q}
        style={{
          background: "rgba(255,255,255,0.04)",
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 12,
          border: "1px solid rgba(255,255,255,0.08)"
        }}
      >
        <summary
          style={{
            fontWeight: 700,
            cursor: "pointer",
            outline: "none",
            color: "#e5e7eb",
            marginBottom: "5px"
          }}
        >
          {qa.q}
        </summary>
        <div
          style={{
            marginTop: 8,
            color: "#f1f5f9",
            lineHeight: 1.5,
            opacity: 0.95,
            whiteSpace: "pre-line"
          }}
        >
          {qa.a}
        </div>
      </details>
    ))}
    <div style={{ fontSize: 14, opacity: 0.9, marginBottom: 10 }}>
      Please provide any questions or feedback{" "}
      <a
        href="https://forms.gle/ioy1MU1gxHcDDLSA8"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#93c5fd", textDecoration: "underline" }}
      >
        here
      </a>.
    </div>
  </section>
);

export default NextStepsModal;
